import { useMemo, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import {
  computeFootballStandings,
  computeOverallStandings,
  computePodium,
} from '../domain';
import type { FootballStanding, PodiumEntry, Standing } from '../domain';
import { useTournament } from '../state/TournamentContext';
import { useTranslation } from '../state/I18nContext';

interface RankRow extends Standing {
  name: string;
  groupLabel: string;
}

interface FootballRankRow extends FootballStanding {
  name: string;
  groupLabel: string;
}

/**
 * Tournament-wide ranking: aggregates every group-stage match across all pools
 * into a single ranked table — matches won / games diff / points for table
 * tennis, the FIFA 3-1-0 table for football.
 */
export function RanksView() {
  const { tournament } = useTournament();
  const { t } = useTranslation();
  const isFootball = tournament?.sportId === 'FOOTBALL';

  const groupByPlayer = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    if (!tournament) return map;
    for (const group of Object.values(tournament.groups)) {
      for (const pid of group.playerIds) map[pid] = group.label;
    }
    return map;
  }, [tournament]);

  const rows = useMemo<RankRow[]>(() => {
    if (!tournament || isFootball) return [];
    return computeOverallStandings(tournament).map((s) => ({
      ...s,
      name: tournament.players[s.playerId]?.name ?? s.playerId,
      groupLabel: groupByPlayer[s.playerId] ?? '—',
    }));
  }, [tournament, isFootball, groupByPlayer]);

  const footballRows = useMemo<FootballRankRow[]>(() => {
    if (!tournament || !isFootball) return [];
    return computeFootballStandings(
      Object.keys(tournament.players),
      Object.values(tournament.cells),
    ).map((s) => ({
      ...s,
      name: tournament.players[s.playerId]?.name ?? s.playerId,
      groupLabel: groupByPlayer[s.playerId] ?? '—',
    }));
  }, [tournament, isFootball, groupByPlayer]);

  const podium = useMemo<PodiumEntry[]>(
    () => (tournament ? computePodium(tournament) : []),
    [tournament],
  );
  const championDecided = podium[0]?.playerId != null;

  const rowCount = isFootball ? footballRows.length : rows.length;
  if (!tournament || rowCount === 0) return null;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {t('tabs.ranks')}
        </Typography>
        <Chip
          variant="outlined"
          label={
            isFootball
              ? t('admin.ranks.teamsChip', { count: rowCount })
              : t('admin.ranks.playersChip', { count: rowCount })
          }
        />
      </Stack>

      {championDecided && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('admin.ranks.finalPodium')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {podium.map((entry) => (
              <PodiumCard
                key={entry.place}
                place={entry.place}
                name={
                  entry.playerId
                    ? tournament.players[entry.playerId]?.name ?? entry.playerId
                    : '—'
                }
              />
            ))}
          </Stack>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t('ranks.overall')}
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isFootball
                ? '44px minmax(140px, 1fr) 64px 44px 40px 40px 40px 48px 48px'
                : '44px minmax(140px, 1fr) 64px 48px 48px 56px',
              columnGap: 2,
              rowGap: 1,
              alignItems: 'center',
              minWidth: isFootball ? 520 : 420,
            }}
          >
            <Head align="center">{t('table.rank')}</Head>
            <Head>{isFootball ? t('table.team') : t('table.player')}</Head>
            <Head align="center">{t('admin.ranks.table.group')}</Head>
            <Head align="right">{t('table.matchesPlayed')}</Head>
            {isFootball ? (
              <>
                <Head align="right">{t('table.won')}</Head>
                <Head align="right">{t('table.drawn')}</Head>
                <Head align="right">{t('table.lost')}</Head>
                <Head align="right">{t('table.goalDiff')}</Head>
                <Head align="right">{t('table.points')}</Head>
              </>
            ) : (
              <>
                <Head align="right">{t('table.wins')}</Head>
                <Head align="right">{t('table.pointsWon')}</Head>
              </>
            )}
            {(isFootball ? footballRows : rows).map((row) => (
              <Box key={row.playerId} sx={{ display: 'contents' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  {row.rank <= 3 ? (
                    <Chip
                      size="small"
                      label={row.rank}
                      color={
                        row.rank === 1
                          ? 'primary'
                          : row.rank === 2
                            ? 'secondary'
                            : 'default'
                      }
                      sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {row.rank}
                    </Typography>
                  )}
                </Box>
                <Tooltip
                  title={t('admin.ranks.playerTooltip', {
                    id: row.playerId,
                    name: row.name,
                  })}
                  arrow
                >
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ fontWeight: row.rank <= 3 ? 700 : 400, minWidth: 0 }}
                  >
                    {row.name}
                  </Typography>
                </Tooltip>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Chip size="small" variant="outlined" label={row.groupLabel} />
                </Box>
                <Num>{row.matchesPlayed}</Num>
                {'points' in row ? (
                  <>
                    <Num>{row.matchesWon}</Num>
                    <Num>{row.matchesDrawn}</Num>
                    <Num>{row.matchesLost}</Num>
                    <Num>{row.goalDiff > 0 ? `+${row.goalDiff}` : row.goalDiff}</Num>
                    <Num>{row.points}</Num>
                  </>
                ) : (
                  <>
                    <Num>{row.matchesWon}</Num>
                    <Num>{row.pointsWon}</Num>
                  </>
                )}
              </Box>
            ))}
          </Box>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1.5, display: 'block' }}
        >
          {isFootball ? t('admin.ranks.footballLegend') : t('admin.ranks.legend')}
        </Typography>
      </Paper>
    </Box>
  );
}

function Head({
  children,
  align = 'left',
}: {
  children: ReactNode;
  align?: 'left' | 'center' | 'right';
}) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      align={align}
      sx={{ fontWeight: 700 }}
    >
      {children}
    </Typography>
  );
}

function Num({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" align="right">
      {children}
    </Typography>
  );
}

const MEDAL_COLOR: Record<1 | 2 | 3, string> = {
  1: '#FFD700', // gold
  2: '#C0C0C0', // silver
  3: '#CD7F32', // bronze
};

function PodiumCard({ place, name }: { place: 1 | 2 | 3; name: string }) {
  const { t } = useTranslation();
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, flex: 1, display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}
    >
      <EmojiEventsIcon sx={{ color: MEDAL_COLOR[place] }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {t(`admin.ranks.place.${place}`)}
        </Typography>
        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700 }}>
          {name}
        </Typography>
      </Box>
    </Paper>
  );
}
